import { useEffect, useRef, useState } from 'react';
import ClassNames from '@streamloots/classnames';
import { TranslateInterface } from 'utils/translation';
import Button, { ButtonIcon } from 'ui-library/button';
import Dialog from 'ui-library/dialog';
import { ConfirmationDialogContent } from 'components/confirmation-dialog';
import { checkPermissions, permissions, staffPermissions } from 'components/permissions';
import theme from './feed-list.scss';
import { useFeedList } from './useFeedList';

const classNames = ClassNames(theme);

export interface FeedActionsProps extends TranslateInterface {
  feedId: string;
  feedLikedCount: number;
  feedCommentCount: number;
}

const FeedActionsBase = ({ feedId, t }: FeedActionsProps): JSX.Element => {
  const { isDeleting, handleDeleteClick, feeds } = useFeedList();
  const [openOption, setOpenOptions] = useState(false);
  const [canDelete, setCanDelete] = useState(true);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const innerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLDivElement | null>(null);

  const openOptions = () => {
    setOpenOptions(true);
  };

  const closeOptions = () => {
    setOpenOptions(false);
  };

  const handleDeleteConfirmOpenClick = () => {
    const canDeleteFeed = feeds?.some(feed => {
      if (feed._id !== feedId) {
        return !feed.restrictedTo.length;
      }
      return feed.restrictedTo.length > 0;
    });

    if (!canDeleteFeed) {
      setCanDelete(false);
    }

    setOpenConfirmation(true);
    closeOptions();
  };

  const closeConfirmation = () => {
    setOpenConfirmation(false);
  };

  const handleConfirmClick = () => {
    handleDeleteClick(feedId);
  };

  useEffect(() => {
    if (!isDeleting) {
      setOpenConfirmation(false);
    }
  }, [isDeleting]);

  useEffect(() => {
    const handleClickOutside = event => {
      if (innerRef.current?.contains(event.target)) {
        return;
      }

      if (inputRef.current?.contains(event.target)) {
        return;
      }

      closeOptions();
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  return (
    <div ref={innerRef} className={classNames('feed-detail__actions')}>
      <ButtonIcon
        icon="dots-horizontals"
        onClick={openOptions}
        className={classNames('feed-detail__actions__button')}
        title={t('pageFeed:feedDetail.showOptions')}
        ariaLabel={t('pageFeed:feedDetail.showOptions')}
      />
      <div
        ref={inputRef}
        className={classNames({
          'feed-detail__actions__options': true,
          'feed-detail__actions__options--hide': !openOption,
        })}
      >
        <Button
          label={t('pageFeed:feedDetail.delete.deleteOption')}
          className={classNames('feed-detail__actions__options__item')}
          onClick={handleDeleteConfirmOpenClick}
          ariaLabel={t('pageFeed:feedDetail.delete.deleteOption')}
        />
      </div>
      <Dialog
        active={openConfirmation}
        renderDialogBody={() => (
          <ConfirmationDialogContent
            onClose={canDelete ? closeConfirmation : undefined}
            title={
              canDelete
                ? t('pageFeed:feedDetail.delete.deleteConfirm')
                : t('pageFeed:feedDetail.delete.cantDeleteTitle')
            }
            confirmationMessage={
              canDelete
                ? t('pageFeed:feedDetail.delete.deleteConfirmDetail')
                : t('pageFeed:feedDetail.delete.cantDeleteDescription')
            }
            confirmButtonText={!canDelete ? t('common:close') : t('pageFeed:feedDetail.delete.deleteConfirmButton')}
            onConfirmClick={canDelete ? handleConfirmClick : closeConfirmation}
            isLoading={isDeleting}
          />
        )}
      />
    </div>
  );
};

export const FeedActions = checkPermissions({
  requiredPermissions: [...staffPermissions, permissions.PAGE_OWNER],
})(FeedActionsBase);
